import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import mealSuggestionService from "@/services/mealSuggestion";
import { DbMealSuggestion, MealSuggestion } from "@/types/mealSuggestion";
import { Skeleton } from "@/components/ui/skeleton";
import { recipeService } from "@/services/recipe";
import { Recipe } from "@/types/recipe";
import { findSimilarRecipes, findExactMatch, SimilarityMatch } from "@/utils/recipeSimilarity";
import { supabase } from "@/integrations/supabase/client";
import { EmptyState } from "@/components/meal-suggestions/EmptyState";
import { MealSuggestionCard } from "@/components/meal-suggestions/MealSuggestionCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PageLayout from "@/components/layout/PageLayout";

export default function MealSuggestions() {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<DbMealSuggestion | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [existingRecipes, setExistingRecipes] = useState<Record<string, Recipe>>({});
  const [familyRecipes, setFamilyRecipes] = useState<Recipe[]>([]);
  const [usageCounts, setUsageCounts] = useState<Record<string, number>>({});
  const [similarMatches, setSimilarMatches] = useState<Record<string, SimilarityMatch[]>>({});
  const [exactMatches, setExactMatches] = useState<Record<string, Recipe>>({});
  const [selectedMealType, setSelectedMealType] = useState<'lunch' | 'dinner'>('dinner');

  useEffect(() => {
    loadLatestSuggestions();
  }, []);

  useEffect(() => {
    loadFamilyRecipes();
  }, []);

  useEffect(() => {
    if (suggestions?.suggestions?.suggestions && familyRecipes.length > 0) {
      findExactMatches();
    }
  }, [suggestions, familyRecipes]);

  useEffect(() => {
    if (suggestions?.suggestions?.suggestions && Object.keys(exactMatches).length >= 0) {
      loadExistingRecipes();
      loadUsageCounts();
      findSimilarMatches();
    }
  }, [suggestions, familyRecipes, exactMatches]);

  const loadLatestSuggestions = async () => {
    setIsLoading(true);
    const response = await mealSuggestionService.getLatestSuggestions();
    
    if (response.success && response.data) {
      setSuggestions(response.data);
    } else {
      console.error('Failed to load meal suggestions:', response.message);
    }
    setIsLoading(false);
  };

  const loadFamilyRecipes = async () => {
    try {
      const recipes = await recipeService.getRecipes();
      setFamilyRecipes(recipes);
    } catch (error) {
      console.error('Failed to load family recipes:', error);
    }
  };

  // ... keep existing code (rest of the helper functions)

  const loadExistingRecipes = async () => {
    if (!suggestions?.suggestions?.suggestions) return;

    const recipeMap: Record<string, Recipe> = {};
    
    for (const suggestion of suggestions.suggestions.suggestions) {
      if (suggestion.isFromCollection && suggestion.recipeId) {
        try {
          const recipe = await recipeService.getRecipeById(suggestion.recipeId);
          if (recipe) {
            recipeMap[suggestion.id] = recipe;
          }
        } catch (error) {
          console.error(`Failed to load recipe ${suggestion.recipeId}:`, error);
        }
      }
    }
    
    setExistingRecipes(recipeMap);
  };

  const loadUsageCounts = async () => {
    if (!suggestions?.suggestions?.suggestions) return;

    const counts: Record<string, number> = {};
    
    for (const suggestion of suggestions.suggestions.suggestions) {
      if (suggestion.recipeId) {
        try {
          const count = await supabase
            .from('meal_plans')
            .select('id', { count: 'exact', head: true })
            .eq('recipe_id', suggestion.recipeId);
          counts[suggestion.id] = count.count || 0;
        } catch (error) {
          console.error(`Failed to load usage count for recipe ${suggestion.recipeId}:`, error);
          counts[suggestion.id] = 0;
        }
      }
    }
    
    setUsageCounts(counts);
  };

  const findExactMatches = () => {
    if (!suggestions?.suggestions?.suggestions || !familyRecipes.length) return;

    const matches: Record<string, Recipe> = {};
    
    suggestions.suggestions.suggestions.forEach(suggestion => {
      if (!suggestion.isFromCollection) {
        const match = findExactMatch(suggestion.recipeName, familyRecipes);
        if (match) {
          matches[suggestion.id] = match;
        }
      }
    });
    
    setExactMatches(matches);
  };

  const findSimilarMatches = () => {
    if (!suggestions?.suggestions?.suggestions || !familyRecipes.length) return;

    const matches: Record<string, SimilarityMatch[]> = {};
    
    suggestions.suggestions.suggestions.forEach(suggestion => {
      if (!suggestion.isFromCollection && !exactMatches[suggestion.id]) {
        const similarRecipes = findSimilarRecipes(suggestion.recipeName, familyRecipes, 3);
        if (similarRecipes.length > 0) {
          matches[suggestion.id] = similarRecipes;
        }
      }
    });
    
    setSimilarMatches(matches);
  };

  const acceptSimilarRecipe = async (suggestionId: string, recipeId: string) => {
    setAcceptingId(suggestionId);
    try {
      const suggestion = suggestions?.suggestions?.suggestions.find(s => s.id === suggestionId);
      if (!suggestion) return;

      const response = await mealSuggestionService.acceptSuggestion({
        ...suggestion,
        recipeId: recipeId,
        isFromCollection: true
      });

      if (response.success) {
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('Error accepting similar recipe:', error);
      toast.error('Failed to accept recipe');
    } finally {
      setAcceptingId(null);
    }
  };

  const generateNewSuggestions = async () => {
    setIsGenerating(true);
    try {
      const response = await mealSuggestionService.generateSuggestions();
      if (response.success) {
        toast.success("New meal suggestions generated!");
        await loadLatestSuggestions();
      } else {
        toast.error(response.message || "Failed to generate suggestions");
      }
    } catch (error) {
      console.error('Error generating suggestions:', error);
      toast.error("Failed to generate suggestions");
    } finally {
      setIsGenerating(false);
    }
  };

  const acceptSuggestion = async (suggestion: MealSuggestion) => {
    setAcceptingId(suggestion.id);
    try {
      // Check if it's an existing recipe from collection
      if (suggestion.isFromCollection && suggestion.recipeId) {
        const response = await mealSuggestionService.acceptSuggestion(suggestion);
        
        if (response.success) {
          toast.success(response.message);
        } else {
          toast.error(response.message);
        }
      } else {
        // For new recipes, we need to preview first
        toast.info("Please preview the recipe details first");
      }
    } catch (error) {
      console.error('Error accepting suggestion:', error);
      toast.error("Failed to accept suggestion");
    } finally {
      setAcceptingId(null);
    }
  };

  const searchAndAddRecipe = (suggestion: MealSuggestion) => {
    if (!suggestion.newRecipeDetails?.searchTerms) {
      toast.error("No search terms available for this recipe");
      return;
    }

    // Navigate to recipe finder with search terms
    const searchQuery = encodeURIComponent(suggestion.newRecipeDetails.searchTerms);
    navigate(`/recipe-finder?q=${searchQuery}`);
  };



  // Filter suggestions by meal type
  const filteredSuggestions = suggestions?.suggestions?.suggestions?.filter(
    (suggestion: MealSuggestion) => suggestion.mealType === selectedMealType
  ) || [];

  const renderSuggestionsList = (mealTypeSuggestions: MealSuggestion[], mealType: 'lunch' | 'dinner') => {
    if (mealTypeSuggestions.length === 0) {
      return (
        <EmptyState 
          onGenerate={generateNewSuggestions}
          isGenerating={isGenerating}
          mealType={mealType}
        />
      );
    }

    return (
      <div className="space-y-4">
        {mealTypeSuggestions.map((suggestion) => (
          <MealSuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            existingRecipe={existingRecipes[suggestion.id]}
            usageCount={usageCounts[suggestion.id]}
            similarMatches={similarMatches[suggestion.id] || []}
            exactMatch={exactMatches[suggestion.id]}
            acceptingId={acceptingId}
            onAccept={acceptSuggestion}
            onAcceptSimilar={(suggestion, recipe) => acceptSimilarRecipe(suggestion.id, recipe.id)}
            onSearch={searchAndAddRecipe}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <PageLayout
        title="AI Meal Suggestions"
        description="Smart meal suggestions for lunch and dinner"
      >
        <div className="space-y-6 pb-14">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!suggestions) {
    return (
      <PageLayout
        title="AI Meal Suggestions"
        description="Smart meal suggestions for lunch and dinner"
      >
        <EmptyState 
          onGenerate={generateNewSuggestions}
          isGenerating={isGenerating}
          mealType="dinner"
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="AI Meal Suggestions"
      description="Smart meal suggestions for lunch and dinner"
    >
      <div className="space-y-6 pb-14">
        <Tabs value={selectedMealType} onValueChange={(value) => setSelectedMealType(value as 'lunch' | 'dinner')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="lunch">Lunch Suggestions</TabsTrigger>
            <TabsTrigger value="dinner">Dinner Suggestions</TabsTrigger>
          </TabsList>
          
          <TabsContent value="lunch" className="space-y-4">
            {renderSuggestionsList(filteredSuggestions, 'lunch')}
          </TabsContent>
          
          <TabsContent value="dinner" className="space-y-4">
            {renderSuggestionsList(filteredSuggestions, 'dinner')}
          </TabsContent>
        </Tabs>

      </div>
    </PageLayout>
  );
}
